/**
 * useUserBadges + MyBadgesProvider — THE data seam between the portal and the
 * `user_badges` backend (public read — badges are public by locked decision 9
 * of the profiles+badges plan).
 *
 * Channel discipline (PR #133 family): the SIGNED-IN user's badge stream has
 * two consumers — their own `/u/:handle` badge wall and the App-level
 * BadgeCelebration overlay. {@link MyBadgesProvider} owns the ONE subscription
 * for MY uid; {@link useUserBadges} stays generic over ARBITRARY uids (the
 * user page views anyone) and derives from the provider when the uid matches,
 * opening its own subscription only for other users.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { UserBadge } from '@djgg/creations';
import { useAuth, useServices } from './context.js';

/** One badge stream's state: whose it is + the rows (`null` = no seed yet). */
export interface MyBadgesState {
  /** The signed-in uid the provider subscribes for (undefined = signed out). */
  uid: string | undefined;
  /** Earned-badge rows, awarded-at ascending. `null` until the seed snapshot. */
  rows: Array<UserBadge & { id: string }> | null;
}

const NO_BADGES: MyBadgesState = { uid: undefined, rows: null };
const MyBadgesContext = createContext<MyBadgesState>(NO_BADGES);

/**
 * Own the signed-in user's ONE `userBadges` subscription and share it via
 * context. Mounted once in App; guests/anonymous sessions subscribe to nothing
 * (locked decision 7: guests earn nothing).
 */
export function MyBadgesProvider({ children }: { children: ReactNode }) {
  const services = useServices();
  const { user } = useAuth();
  const uid = user && !user.isAnonymous ? user.uid : undefined;
  // Seed the CURRENT uid synchronously so a same-uid consumer mounting in the
  // same commit derives from this provider instead of transiently opening its
  // own subscription before the provider's effect runs.
  const [state, setState] = useState<MyBadgesState>(() => ({ uid, rows: null }));

  useEffect(() => {
    setState({ uid, rows: null });
    if (!uid) return;
    return services.subscribe.userBadges(uid, (rows) => setState({ uid, rows }));
  }, [services, uid]);

  return <MyBadgesContext.Provider value={state}>{children}</MyBadgesContext.Provider>;
}

/** The signed-in user's badge stream (the celebration overlay's input). */
export function useMyBadgeRows(): MyBadgesState {
  return useContext(MyBadgesContext);
}

/** Full earned rows keyed by badge id, preserving immutable award dates. */
export function useUserBadgeRows(
  uid: string | undefined,
): ReadonlyMap<string, UserBadge & { id: string }> {
  const services = useServices();
  const my = useContext(MyBadgesContext);
  // The provider already holds a live stream for this uid — derive from it
  // instead of opening a second channel to the same rows.
  const mine = uid !== undefined && my.uid === uid;
  const [ownRows, setOwnRows] = useState<Array<UserBadge & { id: string }> | null>(null);

  useEffect(() => {
    // Reset on identity change so one user's badges never flash on another's
    // page while the new subscription seeds.
    setOwnRows(null);
    if (!uid || mine) return;
    return services.subscribe.userBadges(uid, setOwnRows);
  }, [services, uid, mine]);

  const rows = mine ? my.rows : ownRows;
  return useMemo(() => new Map((rows ?? []).map((row) => [row.badgeId, row])), [rows]);
}

/** Badge-id compatibility projection for count/celebration consumers. */
export function useUserBadges(uid: string | undefined): ReadonlySet<string> {
  const rows = useUserBadgeRows(uid);
  return useMemo(() => new Set(rows.keys()), [rows]);
}
